import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CheckCircle, QrCode, Download, Home } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearSalesFlowSession } from '../../utils/salesSession';
import { downloadReceiptHtml } from '../../utils/receiptDownload';
import { useBranding } from '../../contexts/BrandingContext';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows['2xl']};
  padding: ${props => props.theme.spacing['3xl']};
  width: 100%;
  max-width: 600px;
  text-align: center;
  animation: slideUp 0.5s ease-out;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.successLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.success};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing['2xl']} 0;
`;

const OrderDetails = styled.div`
  background: ${props => props.theme.colors.gray50};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  text-align: left;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.sm};

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textSecondary};
`;

const DetailValue = styled.span`
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const QRCodeContainer = styled.div`
  background: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const QRCodeText = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: ${props => props.theme.spacing.sm} 0 0 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => props.primary 
    ? props.theme.colors.primary 
    : props.theme.colors.white};
  color: ${props => props.primary 
    ? props.theme.colors.white 
    : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    background: ${props => props.primary 
      ? props.theme.colors.primaryDark 
      : props.theme.colors.gray50};
    transform: translateY(-1px);
  }
`;

const OrderComplete = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { branding } = useBranding();
  const [orderData, setOrderData] = useState(location.state?.orderData || null);

  useEffect(() => {
    // If no order data in location state, try to get from sessionStorage
    if (!orderData) {
      const storedOrder = sessionStorage.getItem('orderData');
      if (storedOrder) {
        try {
          setOrderData(JSON.parse(storedOrder));
        } catch (e) {
          console.error('Error parsing stored order:', e);
        }
      }
    }
  }, [orderData]);

  const handleDownload = () => {
    if (!orderData) return;
    let visitorData = {};
    try {
      visitorData = JSON.parse(sessionStorage.getItem('visitorData') || '{}');
    } catch {
      visitorData = {};
    }
    let paymentData = {};
    try {
      paymentData = JSON.parse(sessionStorage.getItem('paymentData') || '{}');
    } catch {
      paymentData = {};
    }
    downloadReceiptHtml({ orderData, paymentData, visitorData, branding });
  };

  const handleNewOrder = () => {
    // Clear the entire in-progress sales flow (incl. visitorFormData) so the
    // next visit opens blank — see utils/salesSession.
    clearSalesFlowSession();
    navigate('/visitor-entry');
  };

  if (!orderData) {
    return (
      <Container>
        <Card>
          <Title>{t('orderComplete.orderComplete')}</Title>
          <Subtitle>{t('orderComplete.loadingOrderDetails')}</Subtitle>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <SuccessIcon>
          <CheckCircle size={40} />
        </SuccessIcon>
        <Title>{t('orderComplete.orderCompletedSuccessfully')}</Title>
        <Subtitle>
          {t('orderComplete.yourOrderHasBeenConfirmed')}
        </Subtitle>

        <OrderDetails>
          <DetailItem>
            <DetailLabel>{t('orderComplete.orderNumber')}</DetailLabel>
            <DetailValue>{orderData.orderNumber || 'N/A'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t('orderComplete.totalAmount')}</DetailLabel>
            <DetailValue>
              {orderData.total?.toLocaleString() || '0'} {t('orderComplete.dzd')}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t('orderComplete.status')}</DetailLabel>
            <DetailValue style={{ 
              color: '#059669',
              textTransform: 'capitalize' 
            }}>
              {orderData.status || 'Completed'}
            </DetailValue>
          </DetailItem>
        </OrderDetails>

        {orderData.barcode && (
          <QRCodeContainer>
            <QrCode size={120} color="#667eea" />
            <QRCodeText>
              {t('orderComplete.orderBarcode')} {orderData.barcode}
            </QRCodeText>
          </QRCodeContainer>
        )}

        <ButtonGroup>
          <Button onClick={handleDownload}>
            <Download size={20} />
            {t('orderComplete.downloadReceipt')}
          </Button>
          <Button primary onClick={handleNewOrder}>
            <Home size={20} />
            {t('orderComplete.newOrder')}
          </Button>
        </ButtonGroup>
      </Card>
    </Container>
  );
};

export default OrderComplete;

